// PersonFirmaList.tsx — Liste „Verbindung zu Unternehmen"
// (Person → Firma). Ein Verein IST hier eine Firma: derselbe Kontakttyp, nur mit
// der Rechtsform e.V. — welche Rollen er anbietet, entscheidet die Matrix über
// `legalFormId` (Mitglied, Vorstand, Kassenwart … statt Geschäftsführer).
// Pro Eintrag: Firma (suchen/eintippen) + volle Verantwortlichkeit-Eingabe
// (intern/extern + GF/Gesellschafter + Anteile + Katalog), wie in der
// Ansprechpartner-Bereinigung. Der „externe Firma"-Unter-Picker entfällt, da die
// Firma hier oben separat gewählt wird.

import * as React from "react";
import { EintragsListe } from "../forms/EintragsListe";
import { ohneAnteil, refName, rollenAnteil, verantText, zeilenAnzeigeVon } from "./beziehungsAnzeige";
import { ContactRefField } from "./ContactRefField";
import { VerantwortlichkeitInput } from "../verantwortlichkeit/VerantwortlichkeitInput";
import type { VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import { useRelStyles } from "./styles";
import { uid } from "./types";
import { AusgeschiedenFelder, ausgeschiedenText } from "./AusgeschiedenFelder";
import type { PersonFirmaItem, ContactRef } from "./types";
import { ZIEH_ART_HAUSHALT, ZIEH_ART_PERSON } from "./HouseholdList";

export function PersonFirmaList({
  value,
  onChange,
  personAblegen,
}: {
  value: PersonFirmaItem[];
  onChange: (next: PersonFirmaItem[]) => void;
  /**
   * Paket A7: Eine Person aus „Haushalt"/„Beziehungen außerhalb des
   * Haushaltes" auf eine gespeicherte Firma ziehen → Verantwortung anlegen.
   * Bekommt den Firmen-Eintrag und die id des Haushalts-/Beziehungs-Eintrags.
   */
  personAblegen?: (firma: PersonFirmaItem, personEintragId: string) => void;
}) {
  return (
    <EintragsListe<PersonFirmaItem>
      items={value}
      onChange={onChange}
      neu={() => ({ id: uid(), ref: null, verant: null })}
      hinzufuegenLabel="Verbindung zu Unternehmen"
      dialogTitel="Verbindung zu Unternehmen"
      zeile={(it) => ({
        titel: refName(it.ref) || "Ohne Namen",
        // Die Quote steht in „Anteile", nicht noch einmal in der Rolle.
        zusatz:
          verantText(
            it.verant ? { ...it.verant, roles: it.verant.roles.map(ohneAnteil) } : null,
          ) || undefined,
        anteile:
          (it.verant?.roles ?? [])
            .map(rollenAnteil)
            .filter(Boolean)
            .join(", ") || undefined,
        ...zeilenAnzeigeVon(it.ref),
        hinweis: ausgeschiedenText(it) || undefined,
        durchgestrichen: !!it.ausgeschieden,
        // Nur eine bereits gespeicherte Firma nimmt eine Person an (A7). Das
        // Ergebnis wird sofort gespeichert — deshalb auch im gesperrten Formular.
        ablage:
          personAblegen && it.ref?.contactId
            ? {
                arten: [ZIEH_ART_HAUSHALT, ZIEH_ART_PERSON],
                onAblage: (daten: string) => personAblegen(it, daten),
                hinweis: "Loslassen: Verantwortung für dieses Unternehmen erfassen",
                auchGesperrt: true,
              }
            : undefined,
      })}
      spalten={{ name: "Unternehmen", rolle: "Verantwortlichkeit", anteile: "Anteile" }}
      leerText="Noch keine Verbindungen erfasst (z. B. Geschäftsführer/Gesellschafter/Verantwortlichkeit)."
      istLeer={(it) => !it.ref}
      renderFelder={(it, update, ctx) => (
        <>
          <PersonFirmaFelder it={it} update={update} neuerEintrag={ctx.entwurf} />
          {/* Nur beim Bearbeiten: wer neu erfasst wird, ist nicht schon ausgeschieden. */}
          {!ctx.entwurf && (
            <AusgeschiedenFelder idBase={`pf-aus-${it.id}`} value={it} onChange={update} />
          )}
        </>
      )}
    />
  );
}

/** Die Felder EINER Verbindung — geteilt zwischen Liste (Popup) und Status-Schritt (inline). */
export function PersonFirmaFelder({
  it,
  update,
  verein = false,
  neuerEintrag,
}: {
  it: PersonFirmaItem;
  update: (patch: Partial<PersonFirmaItem>) => void;
  /** Neue Verbindung: Haken „Mensch im Unternehmen" vorbelegen (Paket A8). */
  neuerEintrag?: boolean;
  /** Beschriftung „Verein" statt „Unternehmen" (Vereinsrollen im Status-Schritt). */
  verein?: boolean;
}) {
  const s = useRelStyles();
  return (
    <>
      <div className={s.refField}>
        <span className={s.sectionLabel}>{verein ? "Verein" : "Unternehmen"}</span>
        <ContactRefField
          wantType="Firma"
          suchPlaceholder={verein ? "Verein suchen …" : "Unternehmen suchen …"}
          idBase={`pf-${it.id}`}
          value={it.ref}
          onChange={(ref: ContactRef | null) => update({ ref })}
          firmaExtraFields={true}
          mitBefuellen={true}
        />
      </div>
      <div className={s.refField}>
        <VerantwortlichkeitInput
          idBase={`pf-verant-${it.id}`}
          hideExternalCompany
          value={it.verant}
          onChange={(v: VerantwortlichkeitValue) => update({ verant: v })}
          // Die Rechtsform der oben gewählten Firma entscheidet, welche
          // Organrollen es gibt.
          legalFormId={it.ref?.legalFormId}
          neuerEintrag={neuerEintrag}
        />
      </div>
    </>
  );
}
